import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import EnumerationComboBoxViewModelType from '@/ViewModels/Controls/EnumerationComboBoxViewModel';
import EnumerationComboBoxType from '@/Controls/EnumerationComboBox';
import LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import DropDownListViewModelType from '@/ViewModels/Controls/DropDownListViewModel';
import Utilities from "@/Utility/Utilities";

export default class EnumerationComboBoxFactory extends ControlFactory {

    private EnumerationComboBoxViewModel = ComponentManager.getViewModel<typeof EnumerationComboBoxViewModelType>('EnumerationComboBoxViewModel');
    private EnumerationComboBox = ComponentManager.getControl<typeof EnumerationComboBoxType>('EnumerationComboBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    private DropDownListViewModel = ComponentManager.getViewModel<typeof DropDownListViewModelType>('DropDownListViewModel');

    private jQueryControl: JQuery<HTMLElement>;
    private sortAlphabetically: KnockoutObservable<boolean>;
    /**
     * @override
     */
    public viewModel: EnumerationComboBoxViewModelType;

    private technicalNameRootProperty = '';
    private technicalNameValueMLS = '';

    public constructor(params) {
        super(params);

        this.sortAlphabetically = this.observableOrDefault(params.sortAlphabetically, false);
    }

    /**
     * @override
     */
    control: EnumerationComboBoxType;

    /**
     * @override
     */
    public getControl() {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.validateTechName();

        this.viewModel = new this.EnumerationComboBoxViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            dropDownList: new this.DropDownListViewModel({
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.select',
                value: this.context.genericObject.getObservable(this.detailViewItem.name),
                data: this.defineComputed(() => {
                    let result: Array<EnumData> = [];

                    if (!this.isRequired()) {
                        result.push({ value: '', caption: ' ', locator: '__empty__' });
                    }
                    else {
                        let value = this.context.genericObject.getValue(this.detailViewItem.name);
                        if (value == null || value == '') {
                            result.push({ value: '', caption: ' ', locator: '__empty__' });
                        }
                    }

                    for (let enumeration in this.detailViewItem.possiblePDEnumerationValues) {
                        let value = this.detailViewItem.possiblePDEnumerationValues[enumeration];
                        
                        // Da hier über das vollständige Objekt propertyweise iteriert wird, müssen wir sicherstellen, dass es sich um ein String handelt!
                        if (!Utilities.isString(value))
                            continue;
                        
                        let caption = ResourceManager.getInstance().loadResource(value, true) as string;
                        result.push({ value: enumeration, caption: caption, locator: enumeration });
                    }

                    if (this.sortAlphabetically()) {
                        result = result.sort((a: EnumData, b: EnumData) => {
                            return a.caption.localeCompare(b.caption);
                        });
                    }

                    return result;
                }),
                itemLocatorStrategy: (item: any, idx: number, $el: JQuery) => {
                    return item.locator;
                },
                dataTextField: 'caption',
                dataValueField: 'value',
                optionLabel: '',
                isEnabled: this.isEnabled,
                isFocusable: this.isFocusable,
                tooltip: new this.LabelViewModel({
                    ergName: this.detailViewItem.tooltip
                }),
                isValid: this.isValid,
                invalidationText: this.invalidationText
            })
        });

        this.control = new this.EnumerationComboBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);

        return this.jQueryControl;
    }

    public getValueForFilter() {
        let ret = {};
        ret[this.detailViewItem.name] = this.context.genericObject.getValue(this.detailViewItem.name);
        return ret;
    }

    public getFilterInfo(): { field: string, value: string } {
        return {
            field: this.technicalNameRootProperty + '.Value',
            value: this.viewModel?.dropDownList.value() || ''
        }
    }

    private validateTechName() {
        this.technicalNameRootProperty = this.detailViewItem.name;

        if (this.detailViewItem.name.substring(this.detailViewItem.name.length - 6) === '.Value') {
            this.technicalNameRootProperty = this.detailViewItem.name.substring(0, this.detailViewItem.name.length - 6);
        }

        this.technicalNameValueMLS = this.technicalNameRootProperty + '.MLSValue';
    }
}

declare type EnumData = {
    value: string,
    caption: string,
    locator: string
}

ComponentManager.setFactory('EnumerationComboBox', EnumerationComboBoxFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');